import * as React from 'react'
import _cloneDeep from 'lodash.clonedeep'
import _omit from 'lodash.omit'
import {
  type BlockChangeChildStatus,
  blockTypesRecord,
  makeInstanceChildBlockChange,
  isBlockWithTitleOverrides,
  Block,
  BlockChange,
  isBlockWithChildren,
  RichTextBlock,
  AgentBlock,
} from '@basehub/data/replicache/index'
import { RichTextBlockDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/rich-text'
import { UseDiffResult } from '~/lib/diff/use-diff'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { DiffSeparator } from './separator'
import { ConflictResolutionControls, DiffChildBlock } from './block/child'
import { DiffLayout } from './diff-layout'
import { PropertiesDiff } from './properties-diff'
import { useSelectedBranch } from '~/lib/commits/use-selected-branch-or-commit'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { InlineTextDiff } from '../../(editor)/explore/components/blocks/rich-text/diff'
import { CommentsPopover } from '../../(editor)/comments'
import { invariant } from '@epic-web/invariant'
import { run } from '@basehub/utils'
import { VariantPills } from '../../(editor)/explore/components/blocks/variant-pills'
import { useIsExternalVisitor } from '~/hooks-data/use-user-role'
import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import { ContributorsTooltip } from '~/components/contributors-tooltip'
import { ContributorsHug } from '../../(editor)/history/_components/sidebar/primitive-commit'
import { flattenContributors } from '@basehub/data/replicache/schema/block-milestone/util'
import { CheckIcon } from '@radix-ui/react-icons'
import { useFlags } from '~/lib/flags/provider'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { useCommitNavigationStore } from '../../(editor)/history/_store'
import { ArticleShortcut } from '~/components/icons/article-shortcut'
import { useGetTabPrefix } from '../../(editor)/explore/components/tabs/store'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { clsx } from 'clsx'
import { SYSTEM_AGENTS } from '@basehub/data/replicache/schema/block/agents'
import { STARTIcon } from '~/components/icons/START'

export const BlockDiff = ({
  blockId,
  diffResult,
  displayBlockLinks = true,
  diffTabPrefix,
  enableComments,
}: {
  blockId: string
  diffResult: UseDiffResult
  displayBlockLinks?: boolean
  diffTabPrefix?: string
  enableComments?: boolean
}) => {
  const { diff, treeOld, treeNew } = diffResult
  const toGetNode = treeOld ? treeOld.getNode : null
  const toGetChildrenKeys = treeOld ? treeOld.getChildrenKeys : null
  const tnGetNode = treeNew?.getNode
  const tnGetChildrenKeys = treeNew?.getChildrenKeys
  const { cr } = useSelectedBranch()
  const replicache = useRepoReplicache().data?.replicache

  // Comments
  const [isHovered, setIsHovered] = React.useState(false)
  const isExternalVisitor = useIsExternalVisitor()

  const change = React.useMemo(() => {
    if (!diff) return null

    const change = diff.changesTree.getNode(blockId)
    if (!change) return null

    return change
  }, [blockId, diff])

  const { data: blockMilestones } = useCustomSyncerSub(replicache, {
    syncer: 'multiBlockMilestones',
    params: {
      rootId:
        treeNew?.root.value.id || (treeOld && treeOld.root.value.id) || '',
      sourceBlockIds: change?.value
        ? [idToSourceBlockId(change.value)]
        : [blockId],
    },
  })
  const blockMilestone = blockMilestones?.[0] ?? null

  React.useEffect(() => {
    const propertiesPanel = document.querySelector<HTMLDivElement>(
      '#document-sidebar-width'
    )

    if (!propertiesPanel) return

    propertiesPanel.style.width = '8px'
    propertiesPanel.style.pointerEvents = 'none'

    return () => {
      propertiesPanel.style.width = 'auto'
      propertiesPanel.style.pointerEvents = 'auto'
    }
  }, [])

  const {
    blocksWithSeparators,
  }: {
    blocksWithSeparators: (
      | DiffSeparator
      | {
          id: string
          status: BlockChangeChildStatus
        }
    )[]
    hasUnmodifiedChildren: boolean | undefined
  } = React.useMemo(() => {
    let currentChildren: string[] = []
    try {
      currentChildren =
        (change?.value.current &&
          tnGetChildrenKeys?.(change.value.current.id)) ||
        []
    } catch (e) {
      currentChildren = []
    }

    if (!change) {
      return { blocksWithSeparators: [], hasUnmodifiedChildren: false }
    }
    const deleted = change.value.children?.filter((c) => {
      if (c.status === 'deleted') return true
      if (c.status === 'conflict') {
        return c.conflictType === 'deleted-in-source'
      }
    })
    if (change.value.current && !isBlockWithChildren(change.value.current)) {
      return { blocksWithSeparators: [], hasUnmodifiedChildren: false }
    }

    const hasUnmodifiedChildren =
      change?.value.children?.length !==
      currentChildren.length + (deleted?.length ?? 0)

    if (!hasUnmodifiedChildren) {
      return {
        blocksWithSeparators: change?.value.children ?? [],
        hasUnmodifiedChildren,
      }
    }

    /**
     * This loop will merge the array of modified/unmodified/created children with the array of deleted children.
     * It'll try to place deleted ones in the same position as they were before being deleted.
     */
    let childrenToRender: string[] = []
    for (
      let index = 0;
      index < Math.max(currentChildren.length, deleted?.length ?? 0);
      index++
    ) {
      const childIdCurrent = currentChildren[index]
      const childIdDeleted = deleted?.[index]?.id
      if (childIdCurrent) {
        invariant(tnGetNode, 'Expected tnGetNode to be defined')
        const child = tnGetNode(childIdCurrent)?.value
        if (child) {
          childrenToRender.push(idToSourceBlockId(child))
        }
      }
      if (childIdDeleted && toGetNode) {
        const deletedNode = toGetNode(childIdDeleted)
        if (deletedNode) {
          const parent = deletedNode.parentKey
            ? toGetNode(deletedNode.parentKey)
            : null
          if (parent && isBlockWithChildren(parent.value)) {
            const parentChildrenKeys = toGetChildrenKeys?.(parent.key) ?? []
            const indexOfDeletedInOldTree = parentChildrenKeys.findIndex(
              (cId) => cId === deletedNode.value.id
            )

            if (indexOfDeletedInOldTree !== -1) {
              childrenToRender.splice(
                indexOfDeletedInOldTree,
                0,
                idToSourceBlockId(deletedNode.value)
              )
            }
          }
        }
      }
    }
    childrenToRender = childrenToRender.filter((c) => c !== undefined)

    const changeChildrenMap = new Map(
      (change.value?.children ?? []).map((item) => [item.id, item])
    )

    return {
      hasUnmodifiedChildren,
      blocksWithSeparators:
        childrenToRender.reduce(
          (accum, cId, idx, children) => {
            const currentBlock = tnGetNode?.(cId)?.value
            const currentBlockId = currentBlock
              ? idToSourceBlockId(currentBlock)
              : cId

            const child =
              changeChildrenMap.get(cId) ??
              (currentBlock
                ? changeChildrenMap.get(idToSourceBlockId(currentBlock))
                : null)
            const prevChildInArray = children[idx - 1]
            const prevChild = !!prevChildInArray
              ? changeChildrenMap.get(prevChildInArray)
              : null

            if (!prevChild) {
              // if there is no previous child, and the current child is unmodified (didn't find it in the change, because we're not including it to the list), we only append it to the accumulator
              if (child) return [...accum, child]

              // if the current child is unmodified, we create a separator
              const last = accum[accum.length - 1]
              const brandNewSeparator = [
                ...accum,
                {
                  status: 'separator' as const,
                  blocksInside: [currentBlockId],
                },
              ]

              if (!last || last.status !== 'separator') return brandNewSeparator

              const lastSeparator = accum.pop()
              if (!lastSeparator || last.status !== 'separator') {
                return brandNewSeparator
              }

              if (lastSeparator.status === 'separator') {
                return [
                  ...accum,
                  {
                    ...lastSeparator,
                    blocksInside: [
                      ...lastSeparator.blocksInside,
                      currentBlockId,
                    ],
                  },
                ]
              }
            }

            if (!child && !prevChild) {
              // Case 1: Current block is unmodified, and previous block is unmodified
              // we need to append the current block to the previous separator
              const newAccum = [...accum]
              const lastItem = newAccum[newAccum.length - 1]
              const blocksInside =
                lastItem?.status === 'separator' ? lastItem?.blocksInside : []
              newAccum.splice(newAccum.length - 1, 1, {
                status: 'separator',
                blocksInside: [...blocksInside, currentBlockId],
              })
              return newAccum
            }

            // !child means unmodified
            if (!child) {
              // case 2: if only the current block is unmodified, we create a separator
              const newAccum = [...accum]
              newAccum.push({
                status: 'separator',
                blocksInside: [currentBlockId],
              })
              return newAccum
            }

            if (!prevChild) {
              // Case 3: previous block is unmodified, and current block is modified
              const newAccum = [...accum]
              newAccum.push(child)
              return newAccum
            }

            // Case 4: Current block and previous block are modified
            const newAccum = [...accum]
            newAccum.push(child)
            return newAccum
          },
          [] as (
            | DiffSeparator
            | { id: string; status: BlockChangeChildStatus }
          )[]
        ) ?? [],
    }
  }, [change, toGetNode, tnGetNode, toGetChildrenKeys, tnGetChildrenKeys])

  const [separators, setSeparators] = React.useState<
    (DiffSeparator & { open: boolean })[]
  >([])

  React.useEffect(() => {
    // we need to update the separators when the blocksWithSeparators change without lost the open state
    const separatorsCopy = _cloneDeep(separators)
    const newSeparators = blocksWithSeparators.reduce(
      (accum, block) => {
        if (block.status === 'separator') {
          const separatorIndex = separatorsCopy.findIndex(
            (s) =>
              [...s.blocksInside].sort().join('-') ===
              [...block.blocksInside].sort().join('-')
          )
          if (separatorIndex === -1) {
            accum.push({
              ...block,
              open: false,
            })
          } else {
            accum.push({
              ...block,
              open: separatorsCopy[separatorIndex]?.open ?? false,
            })
            separatorsCopy.splice(separatorIndex, 1)
          }
        }
        return accum
      },
      [] as (DiffSeparator & { open: boolean })[]
    )
    setSeparators(newSeparators)

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocksWithSeparators])

  const handleToggleSeparator = React.useCallback(
    (separator: DiffSeparator) => {
      setSeparators((prevSeparators) => {
        const separatorIndex = prevSeparators.findIndex(
          (s) =>
            [...s.blocksInside].sort().join('-') ===
            [...separator.blocksInside].sort().join('-')
        )
        if (separatorIndex === -1) return prevSeparators

        const newSeparators = _cloneDeep(prevSeparators)
        newSeparators[separatorIndex]!.open =
          !newSeparators[separatorIndex]!.open

        return newSeparators
      })
    },
    []
  )

  const renderDocumentChanges = React.useMemo(() => {
    if (blocksWithSeparators.length === 0 && change?.value.previous !== null) {
      return (
        <i className="text-dark-faint text-sm font-medium">
          This {change?.value.type} is empty
        </i>
      )
    }

    return blocksWithSeparators.map((c, i) => {
      if (!diff) return null

      if (c.status === 'separator') {
        return (
          <DiffSeparator
            enableComments={enableComments}
            key={`separator-${i}`}
            blocks={c.blocksInside}
            treeNew={treeNew}
            treeOld={treeOld || undefined}
            changesTree={diff?.changesTree}
            displayBlockLinks={displayBlockLinks}
            diff={diff}
            width="100vw"
            handleToggle={() => handleToggleSeparator(c)}
            isOpen={
              separators.find(
                (s) =>
                  [...s.blocksInside].sort().join('-') ===
                  [...c.blocksInside].sort().join('-')
              )?.open ?? false
            }
          />
        )
      }

      let child = diff.changesTree.getNode(c.id)?.value
      if (!child) {
        getUnomdifiedBlockChange({
          changeId: c.id,
          treeNew: treeNew,
          treeOld: treeOld,
        })
      }
      if (!child) return null

      if (change) {
        /**
         * parent is instance. i should get the "enhanced" previous title and new title.
         *
         * - if it's deleted, then there's no "new"
         * - if it's created, then there's no "previous"
         */
        child = makeInstanceChildBlockChange({
          child,
          treeNew,
          treeOld,
        })
      }

      return (
        <DiffChildBlock
          commentsControls={enableComments}
          key={c.id}
          statusFromParent={c.status}
          child={child}
          index={i}
          displayBlockLink={displayBlockLinks}
          changesTree={diff?.changesTree}
          treeNew={treeNew}
          treeOld={treeOld || undefined}
          diffTabPrefix={diffTabPrefix}
        />
      )
    })
  }, [
    blocksWithSeparators,
    diff,
    displayBlockLinks,
    handleToggleSeparator,
    separators,
    treeNew,
    treeOld,
    change,
    diffTabPrefix,
    enableComments,
  ])

  const { branching } = useFlags()
  const isCheckingOutThisCommit = useCommitNavigationStore(
    (s) => s.isCheckingOutThisCommit
  )
  const currentHref = useCommitNavigationStore((s) => s.currentHref)
  const previousHref = useCommitNavigationStore((s) => s.previousHref)
  // const previousCommit = useCommitNavigationStore((s) => s.previousCommit)
  // const selectedCommit = useCommitNavigationStore((s) => s.currentCommit)
  const tabPrefix = useGetTabPrefix()
  const isCommitTab = tabPrefix.startsWith('commit:')

  if (!change) return null

  if (
    change.value.type === blockTypesRecord.document ||
    change.value.type === blockTypesRecord.component ||
    change.value.type === blockTypesRecord.instance ||
    change.value.type === blockTypesRecord.list ||
    change.value.type === blockTypesRecord.richText ||
    change.value.type === blockTypesRecord.reference ||
    change.value.type === blockTypesRecord.agent
  ) {
    const [oldNode, newNode] = [
      treeOld
        ? treeOld.getNode(change.value.id, {
            fallbackKey: 'sourceBlockId',
          })
        : null,
      treeNew?.getNode(change.value.id, {
        fallbackKey: 'sourceBlockId',
      }),
    ]
    const [oldParentNode, newParentNode] = [
      treeOld && oldNode?.parentKey
        ? treeOld.getNode(oldNode?.parentKey)
        : null,
      newNode?.parentKey ? treeNew?.getNode(newNode?.parentKey) : null,
    ]

    const isConflictOnTitle =
      change.value.status === 'conflict' &&
      (change.value.conflictPaths.includes('title') ||
        change.value.conflictType === 'deleted-in-source' ||
        change.value.conflictType === 'deleted-in-head')

    let previousBlock = change.value.previous
    const conflictOrPrevious = change.value.conflicting ?? previousBlock

    let previousBlockTitle =
      change.value.status === 'created'
        ? ''
        : conflictOrPrevious && treeOld
          ? conflictOrPrevious?.title ||
            getBlockDisplayData({
              block: conflictOrPrevious,
              parentBlock: oldParentNode?.value,
            }).displayTitle
          : 'Untitled'

    const currentBlockTitle =
      change.value.status === 'deleted' ||
      (change.value.status === 'conflict' &&
        change.value.conflictType === 'deleted-in-source')
        ? ''
        : change.value.current && treeNew
          ? change.value.current.title ||
            getBlockDisplayData({
              block: change.value.current,
              parentBlock: newParentNode?.value,
            }).displayTitle
          : 'Untitled'

    const { titleOverrides, isConflictOnOverrides } = run(() => {
      if (
        !isBlockWithTitleOverrides(
          (change.value.current ?? change.value.previous)!,
          newParentNode?.value ?? oldParentNode?.value
        )
      ) {
        return { isConflictOnOverrides: false, titleOverrides: [] }
      }

      invariant(
        !change.value.current ||
          isBlockWithTitleOverrides(change.value.current, newParentNode?.value),
        'current block is not an instance'
      )
      invariant(
        !change.value.previous ||
          isBlockWithTitleOverrides(
            change.value.previous,
            oldParentNode?.value
          ),
        'previous block is not an instance'
      )

      const acc: Array<{
        oKey: string
        currentOverrideTitle: string
        previousOverrideTitle: string | null
      }> = []
      for (const [key, override] of Object.entries(
        change.value.current?.variantOverrides ?? {}
      )) {
        acc.push({
          oKey: key,
          currentOverrideTitle: override.title,
          previousOverrideTitle:
            change.value.previous?.variantOverrides?.[key]?.title ?? null,
        })
      }
      for (const [key, override] of Object.entries(
        change.value.previous?.variantOverrides ?? {}
      )) {
        if (!change.value.current?.variantOverrides?.[key]) {
          acc.push({
            oKey: key,
            currentOverrideTitle: '',
            previousOverrideTitle: override.title,
          })
        }
      }
      return {
        titleOverrides: acc,
        isConflictOnOverrides:
          change.value.status === 'conflict' &&
          change.value.conflictPaths.includes('variantOverrides'),
      }
    })

    const isInFullView =
      change.value.type === blockTypesRecord.richText ||
      change.value.type === blockTypesRecord.list ||
      change.value.type === blockTypesRecord.reference

    let child: BlockChange | null | undefined = diff?.changesTree?.getNode(
      change.value.id
    )?.value
    if (!child) {
      child = getUnomdifiedBlockChange({
        changeId: change.value.id,
        treeNew: treeNew!,
        treeOld: treeOld!,
      })?.value
    }
    invariant(child, 'child is not found')

    let shouldShowPreviousTitle = change.value.status !== 'created'
    if (change.value.type === 'agent' && !previousBlock) {
      const systemFallback = SYSTEM_AGENTS.find(
        (a) => a.id === change.value.current?.sourceBlockId
      )
      if (systemFallback) {
        shouldShowPreviousTitle = true
        previousBlock = systemFallback as AgentBlock
        previousBlockTitle = systemFallback.title || 'Untitled'
      }
    }

    return (
      <>
        <div
          data-document-title={idToSourceBlockId(
            (change.value?.current || change.value?.previous)!
          )}
          className="grid grid-cols-2 gap-x-16 relative gap-y-6"
          onMouseEnter={() => {
            setIsHovered(true)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
          }}
        >
          {shouldShowPreviousTitle ? (
            <div className="text-dark-faint flex items-center justify-between col-start-1 relative">
              {!!(previousHref && isCommitTab) && (
                <div className="flex flex-col gap-y-1 absolute -top-6 left-0">
                  {/* {previousCommit && (
                    <div>
                      <CommitDisplay
                        commit={previousCommit}
                        avatarSize="petite"
                        truncateMessage={true}
                        messageClassName="max-w-[300px]"
                      />
                    </div>
                  )} */}
                  <ButtonLink
                    unstyled
                    focusStyled
                    href={previousHref}
                    className="flex items-center text-dark-faint text-xs font-medium gap-1 cursor-default hover:text-dark-bright transition-colors"
                  >
                    <ArticleShortcut className="shrink-0 size-3.5" />
                    <span>Browse repo at this time (previous commit)</span>
                  </ButtonLink>
                </div>
              )}
              {previousBlock?.type === 'agent' && (
                <figure
                  className={clsx(
                    'overflow-hidden mr-1.5 relative row-span-full object-cover size-10 border border-dark-border-solid rounded-lg bg-dark-bg',
                    previousBlock.typeOptions.avatar !==
                      (change.value.current as AgentBlock | null)?.typeOptions
                        .avatar && '!border-red-diff'
                  )}
                >
                  {previousBlock.typeOptions.avatar ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={previousBlock.typeOptions.avatar}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="object-cover w-full h-full pointer-events-none"
                    />
                  ) : (
                    <STARTIcon className="absolute size-8 inset-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                  )}
                </figure>
              )}
              <h2
                className="text-2xl font-medium place-self-start leading-[1.2] mb-2 flex-1"
                style={{ wordBreak: 'break-word' }}
              >
                <InlineTextDiff
                  condition="removed"
                  text={currentBlockTitle}
                  textMatch={previousBlockTitle}
                  isConflictingText={isConflictOnTitle}
                />
              </h2>
            </div>
          ) : (
            <div className="col-start-1" />
          )}

          {change.value.status !== 'deleted' && (
            <div className="flex items-start justify-between relative col-start-2">
              {Boolean(branching && isCommitTab) && (
                <div className="flex flex-col gap-y-1 absolute -top-6 left-0">
                  {/* {selectedCommit && (
                    <CommitDisplay
                      commit={selectedCommit}
                      avatarSize="petite"
                      truncateMessage={true}
                      messageClassName="max-w-[300px]"
                    />
                  )} */}
                  {isCheckingOutThisCommit ? (
                    <p className="flex items-center text-dark-control-base text-xs font-medium gap-1">
                      <CheckIcon className="shrink-0 size-3.5 -translate-y-px" />
                      <span>Checked out</span>
                    </p>
                  ) : (
                    <ButtonLink
                      unstyled
                      focusStyled
                      href={currentHref}
                      className="flex items-center text-dark-faint text-xs font-medium gap-1 cursor-default hover:text-dark-bright transition-colors"
                    >
                      <ArticleShortcut className="shrink-0 size-3.5" />
                      <span>Browse repo at this time</span>
                    </ButtonLink>
                  )}
                </div>
              )}
              {change.value.current?.type === 'agent' && (
                <figure
                  className={clsx(
                    'overflow-hidden mr-1.5 relative row-span-full object-cover size-10 border border-dark-border-solid rounded-lg bg-dark-bg',
                    (previousBlock as AgentBlock | null)?.typeOptions.avatar !==
                      change.value.current.typeOptions.avatar &&
                      '!border-green-diff'
                  )}
                >
                  {change.value.current.typeOptions.avatar ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={change.value.current.typeOptions.avatar}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="object-cover w-full h-full pointer-events-none"
                    />
                  ) : (
                    <STARTIcon className="absolute size-8 inset-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                  )}
                </figure>
              )}
              <h2 className="text-2xl font-medium place-self-start leading-[1.2] mb-2 flex-1 overflow-hidden relative">
                <InlineTextDiff
                  condition="added"
                  text={currentBlockTitle}
                  textMatch={previousBlockTitle}
                  isConflictingText={isConflictOnTitle}
                />
                {enableComments && !isExternalVisitor && (
                  <div className="absolute left-full -top-1 h-full w-[32px] px-2">
                    <CommentsPopover
                      className="!translate-y-0 "
                      block={
                        treeNew?.getNode(change.value.id)?.value ?? change.value
                      }
                      isVisible={isHovered}
                      type="diff"
                    />
                  </div>
                )}
              </h2>
              {blockMilestone && (
                <ContributorsTooltip
                  content={change.value.status}
                  blockMilestone={blockMilestone}
                >
                  <ContributorsHug
                    contributors={flattenContributors(
                      blockMilestone.contributors
                    )}
                    disableTooltip
                  />
                </ContributorsTooltip>
              )}
            </div>
          )}
          {shouldShowPreviousTitle &&
            titleOverrides.map((override, order) =>
              override.previousOverrideTitle ? (
                <div
                  key={override.oKey}
                  style={{ order }}
                  className="flex flex-col gap-y-2 overflow-hidden col-start-1"
                >
                  <div className="flex gap-x-2">
                    <VariantPills variants={override.oKey} />
                  </div>
                  <h3 className="text-2xl text-dark-faint font-medium place-self-start leading-[1.2] mb-2 flex-1 truncate w-full">
                    <InlineTextDiff
                      condition="removed"
                      text={override.currentOverrideTitle}
                      textMatch={override.previousOverrideTitle}
                      isConflictingText={isConflictOnOverrides}
                    />
                  </h3>
                </div>
              ) : (
                <div
                  key={override.oKey}
                  className="col-start-1"
                  style={{ order }}
                />
              )
            )}
          {change.value.status !== 'deleted' &&
            titleOverrides.map((override, order) => (
              <div
                key={override.oKey}
                style={{ order }}
                className="flex flex-col gap-y-2 overflow-hidden col-start-2"
              >
                <div className="flex gap-x-2">
                  <VariantPills variants={override.oKey} />
                </div>
                <h3
                  className="text-2xl font-medium place-self-start leading-[1.2] mb-2 flex-1 w-full"
                  style={{ wordBreak: 'break-word' }}
                >
                  <InlineTextDiff
                    condition="added"
                    text={override.currentOverrideTitle}
                    textMatch={override.previousOverrideTitle ?? ''}
                    isConflictingText={isConflictOnOverrides}
                  />
                </h3>
              </div>
            ))}
        </div>

        {/* Switches */}
        {/* {shouldDisplaySwitch && (
            <DiffSeparatorSwitch
              hasUnmodifiedChildren={hasUnmodifiedChildren}
              separators={separators}
              onCheckedChange={toggleAllSeparators}
            />
          )} */}

        <div className="grid grid-cols-2 gap-x-16">
          <PropertiesDiff
            wrapper={({ children }) => <div className="mt-2">{children}</div>}
            condition="removed"
            change={{ ...change.value, previous: previousBlock } as BlockChange}
            treeNew={treeNew}
            treeOld={treeOld || undefined}
            changesTree={diff?.changesTree}
          />
          <PropertiesDiff
            wrapper={({ children }) => (
              <div className="mt-2 col-start-2">{children}</div>
            )}
            condition="added"
            change={{ ...change.value, previous: previousBlock } as BlockChange}
            treeNew={treeNew}
            treeOld={treeOld || undefined}
            changesTree={diff?.changesTree}
          />
        </div>

        {change.value.status === 'conflict' && cr && (
          <div className="flex items-center justify-center w-full">
            <ConflictResolutionControls cr={cr} change={change.value} />
          </div>
        )}

        {isInFullView ? (
          <DiffLayout className="!mb-0">
            {diff && (
              <DiffChildBlock
                statusFromParent={change.value.status}
                child={child}
                index={0}
                displayBlockLink={displayBlockLinks}
                changesTree={diff?.changesTree}
                treeNew={treeNew}
                treeOld={treeOld || undefined}
                diffTabPrefix={diffTabPrefix}
                commentsControls={enableComments}
                isInFullView
              />
            )}
          </DiffLayout>
        ) : (
          <DiffLayout>
            {change.value.type === 'agent' && (
              <>
                {previousBlock && (
                  <RichTextBlockDiff
                    status={change.value.status}
                    changesTree={diff?.changesTree}
                    treeOld={treeOld || undefined}
                    current={change.value.current as RichTextBlock}
                    previous={previousBlock as RichTextBlock | null}
                    conflicting={
                      change.value.conflicting as RichTextBlock | null
                    }
                    change={change.value}
                    condition="removed"
                    className="col-start-1"
                  />
                )}

                <RichTextBlockDiff
                  status={change.value.status}
                  changesTree={diff?.changesTree}
                  treeOld={treeOld || undefined}
                  current={
                    (change.value.current ?? previousBlock) as RichTextBlock
                  }
                  previous={previousBlock as RichTextBlock | null}
                  conflicting={change.value.conflicting as RichTextBlock | null}
                  change={change.value}
                  condition="added"
                  className={clsx(
                    change.value.status === 'deleted' && 'opacity-30',
                    'col-start-3'
                  )}
                />
              </>
            )}
            {renderDocumentChanges}
          </DiffLayout>
        )}
      </>
    )
  }
}

export const getUnomdifiedBlockChange = ({
  changeId,
  treeNew,
  treeOld,
}: {
  changeId: BlockChange['id']
  treeNew: TreeManager<Block, NonNullable<unknown>>
  treeOld: TreeManager<Block, NonNullable<unknown>> | false
}): TreeNode<BlockChange> | null => {
  const prevChild = treeOld ? (treeOld.getNode(changeId) ?? null) : null
  const currentChild = treeNew?.getNode(changeId) ?? null
  const prevChildPath = treeOld ? treeOld.getPath(changeId) : ''
  const currentChildPath = treeNew?.getPath(changeId) ?? ''

  const type = currentChild?.value.type ?? prevChild?.value.type
  if (!currentChild || !prevChild || !type) return null

  return {
    parentKey: currentChild?.parentKey ?? prevChild?.parentKey ?? null,
    key: changeId,
    order: currentChild?.order ?? prevChild?.order ?? 'a0',
    value: {
      id: changeId,
      children: [],
      conflicting: null,
      current: currentChild.value,
      previous: prevChild.value,
      path: currentChildPath ?? prevChildPath,
      title: currentChild?.value.title ?? prevChild?.value.title ?? '',
      type,
      status: 'unmodified',
    },
  }
}
